import type { CommandAgent } from "@/types/command-agent";

export type IntegrationLookupRow = {
    status?: string;
    name?: string;
    third_party_app?: { name?: string };
    id?: string;
    integration_id?: string;
};

export function formatIntegrationList(names: string[]): string {
    if (names.length === 0) return "";
    if (names.length === 1) return names[0];
    if (names.length === 2) return `${names[0]} and ${names[1]}`;
    return `${names[0]}, ${names[1]}, and ${names.length - 2} more`;
}

export function integrationIdFromRow(row: { id?: string; integration_id?: string }) {
    return row.integration_id || row.id || "";
}

export function rowMatchesSelected(
    row: { id?: string; integration_id?: string },
    selected: Set<string>
) {
    return Boolean(
        (row.id && selected.has(row.id)) || (row.integration_id && selected.has(row.integration_id))
    );
}

export function dedupeIntegrationNames(names: string[]): string[] {
    return Array.from(new Map(names.map((name) => [name.toLowerCase(), name])).values());
}

export function namesFromRows(
    rows: IntegrationLookupRow[],
    predicate: (status: string) => boolean
) {
    const names = rows
        .filter((item) => predicate((item?.status ?? "").trim().toUpperCase()))
        .map((item) => (item.name || item.third_party_app?.name || "App").trim())
        .filter(Boolean);
    return dedupeIntegrationNames(names);
}

export function getSelectedIntegrationIds(agent?: CommandAgent | null): string[] {
    const list = agent?.app_integrations;
    if (!Array.isArray(list) || list.length === 0) return [];

    if (typeof list[0] === "string") {
        return Array.from(
            new Set((list as unknown as string[]).filter((id): id is string => Boolean(id)))
        );
    }

    const ids = (list as unknown as IntegrationLookupRow[])
        .map((row) => integrationIdFromRow(row))
        .filter(Boolean);
    return Array.from(new Set(ids));
}

export function getAgentIntegrationRows(agent?: CommandAgent | null): IntegrationLookupRow[] {
    const list = agent?.app_integrations;
    if (!Array.isArray(list) || list.length === 0 || typeof list[0] === "string") {
        return [];
    }
    return list as unknown as IntegrationLookupRow[];
}

export function getExpiredNamesFromAgentInline(agent?: CommandAgent | null): string[] {
    if (!agent) return [];

    const names: string[] = [];

    for (const app of agent.app_integrations ?? []) {
        if (typeof app === "string") continue;
        if ((app.status ?? "").trim().toUpperCase() === "EXPIRED") {
            names.push((app.name || app.alias || "App").trim());
        }
    }

    for (const app of agent.custom_apps ?? []) {
        if ((app.status ?? "").trim().toUpperCase() === "EXPIRED") {
            names.push((app.name || "App").trim());
        }
    }

    return dedupeIntegrationNames(names.filter(Boolean));
}

export function resolveStatusRows(
    agent: CommandAgent | null | undefined,
    lookupItems: IntegrationLookupRow[]
): IntegrationLookupRow[] {
    const selectedIntegrationIds = getSelectedIntegrationIds(agent);
    const agentRows = getAgentIntegrationRows(agent);
    const selected = new Set(selectedIntegrationIds);
    const lookupById = new Map<string, IntegrationLookupRow>();

    lookupItems.forEach((row) => {
        if (row.id) lookupById.set(row.id, row);
        if (row.integration_id) lookupById.set(row.integration_id, row);
    });

    if (agentRows.length > 0) {
        const hasAnyAgentRowIds = agentRows.some((row) => Boolean(row.id || row.integration_id));
        return agentRows
            .map((row) => {
                if (row.status?.trim()) return row;
                const lookupKey = row.id || row.integration_id || "";
                return (lookupKey && lookupById.get(lookupKey)) || row;
            })
            .filter((row) => (hasAnyAgentRowIds ? rowMatchesSelected(row, selected) : true));
    }

    if (lookupItems.length === 0 || selected.size === 0) return [];

    return lookupItems.filter((row) => rowMatchesSelected(row, selected));
}

export function resolveExpiredIntegrationNames(
    statusRows: IntegrationLookupRow[],
    options?: { testPreviewWithActive?: boolean }
): { expiredNames: string[]; isTestPreview: boolean } {
    const testPreviewWithActive = options?.testPreviewWithActive ?? false;

    if (!statusRows.length) {
        return { expiredNames: [], isTestPreview: false };
    }

    if (testPreviewWithActive) {
        const activeNames = namesFromRows(statusRows, (status) => status === "ACTIVE");
        if (activeNames.length > 0) {
            return { expiredNames: activeNames, isTestPreview: true };
        }
        const anyNames = namesFromRows(statusRows, () => true);
        return { expiredNames: anyNames, isTestPreview: anyNames.length > 0 };
    }

    return {
        expiredNames: namesFromRows(statusRows, (status) => status === "EXPIRED"),
        isTestPreview: false,
    };
}

/** Resolve expired app names for one agent using inline payload + integrations lookup. */
export function getExpiredIntegrationNamesForAgent(
    agent?: CommandAgent | null,
    lookupItems: IntegrationLookupRow[] = []
): string[] {
    if (!agent) return [];

    const statusRows = resolveStatusRows(agent, lookupItems);
    const { expiredNames } = resolveExpiredIntegrationNames(statusRows);
    if (expiredNames.length > 0) return expiredNames;

    return getExpiredNamesFromAgentInline(agent);
}
